import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import locationsData from "consts/locations.json";
import { useEffect, useState } from "react";
import stores from "consts/stores.json";

// Utility function to merge Tailwind CSS classes
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const isDropoffStore = (user) => {
  const dropoffStores = ["PA", "NJ"];
  return dropoffStores.includes(user.state);
};

export const shouldShowShippingTab = (user) => {
  const shippingStates = ["CO", "AR"];

  return user?.state && shippingStates.includes(user.state);
};

export const checkIfFullKiosk = (currentLocation) => {
  if (!currentLocation?.takebackMethod) return false;

  return stores.some(
    (store) =>
      currentLocation.takebackMethod.includes(store.value) &&
      store.return_type === "kiosk"
  );
};

// Utility function to format dates for SNOW
export function formatSnowDate(date) {
  const padZero = (num) => num.toString().padStart(2, "0");

  const year = date.getFullYear();
  const month = padZero(date.getMonth() + 1); // Months are zero-based
  const day = padZero(date.getDate());
  const hours = padZero(date.getHours());
  const minutes = padZero(date.getMinutes());
  const seconds = padZero(date.getSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// Utility function to get all query parameters and convert numerical and boolean values
export function getQueryParams(search) {
  const params = new URLSearchParams(search);
  const queryParams = {};

  for (let [key, value] of params.entries()) {
    // Add additional type conversions if necessary
    if (!isNaN(parseFloat(value)) && isFinite(value)) {
      queryParams[key] = parseFloat(value);
    } else if (value.toLowerCase() === "true") {
      queryParams[key] = true;
    } else if (value.toLowerCase() === "false") {
      queryParams[key] = false;
    } else {
      queryParams[key] = value;
    }
  }

  return { ...queryParams };
}

export function checkPasswordCriteria(password) {
  const passwordCriteria = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
  return passwordCriteria.test(password);
}

export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function formatEstimationNumber(value, decimalPlaces = 2) {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? value : parsedValue.toFixed(decimalPlaces);
}

export function calculateDurationByWeight(weight) {
  if (weight <= 20) return 30;

  // For weights over 20 lbs: 30 minutes base + 15 minutes per additional 20 lbs (or part thereof)
  const additionalWeight = weight - 20;
  const additionalBlocks = Math.ceil(additionalWeight / 20);
  const calculatedDuration = 30 + additionalBlocks * 15;

  // Cap the maximum duration at 90 minutes
  return Math.min(calculatedDuration, 90);
}

export function getKgInLbs(weight) {
  const lbsWeight = weight * 2.2046226218488;
  // Round to one decimal point
  const roundedWeight = Math.round(lbsWeight * 10) / 10;
  // Convert to string and ensure it has two decimal places if needed
  const formattedWeight = roundedWeight.toFixed(2);
  return parseFloat(formattedWeight);
}

function checkState() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const colorado = {
          north: 40.359075,
          south: 39.249104,
          east: -104.142595,
          west: -105.487599,
        };
        const newJersey = {
          north: 41.281779,
          south: 40.221627,
          east: -73.653103,
          west: -75.199328,
        };
        const pennsylvania = {
          north: 41.167048,
          south: 40.031942,
          east: -79.177588,
          west: -80.747519,
        };
        const arkansas = {
          north: 36.718376,
          south: 35.861143,
          east: -93.659099,
          west: -94.826463,
        };

        if (
          latitude >= colorado.south &&
          latitude <= colorado.north &&
          longitude >= colorado.west &&
          longitude <= colorado.east
        ) {
          resolve("CO");
        } else if (
          latitude >= newJersey.south &&
          latitude <= newJersey.north &&
          longitude >= newJersey.west &&
          longitude <= newJersey.east
        ) {
          resolve("NJ");
        } else if (
          latitude >= pennsylvania.south &&
          latitude <= pennsylvania.north &&
          longitude >= pennsylvania.west &&
          longitude <= pennsylvania.east
        ) {
          resolve("PA");
        } else if (
          latitude >= arkansas.south &&
          latitude <= arkansas.north &&
          longitude >= arkansas.west &&
          longitude <= arkansas.east
        ) {
          resolve("AR");
        } else {
          resolve(null);
        }
      },
      (error) => {
        reject(error);
      }
    );
  });
}

export function useCurrentLocation() {
  const [location, setLocation] = useState({});
  const [error, setError] = useState(null);
  const queryParams = new URLSearchParams(window.location.search);
  const stateQueryParam = queryParams.get("state");
  const language = window.location.pathname.split("/")[1];

  //initial useEffect to set location based on language
  useEffect(() => {
    setLocation(
      locationsData.find((loc) => loc.locale === language && loc.state === null)
    );
  }, [language]);

  //useEffect to set location based on state query param if present
  useEffect(() => {
    async function fetchLocation() {
      try {
        const currentState =
          language.toLowerCase() === "en-us"
            ? stateQueryParam || (await checkState())
            : null;

        const foundLocation =
          locationsData.find(
            (loc) =>
              loc.locale === language &&
              loc.state?.toLowerCase() === currentState?.toLowerCase()
          ) ||
          locationsData.find(
            (loc) => loc.locale === language && loc.state === null
          );

        setLocation(foundLocation);
      } catch (err) {
        setError(err);
      }
    }

    fetchLocation();
  }, [stateQueryParam, language]);

  return {
    location: {
      name: "",
      takebackMethod: [],
      takebackDisbursement: [],
      locale: "",
      country: language || null,
      state: stateQueryParam || null,
      ...location,
    },
    error,
  };
}

//get German decimal separator format
export const localizeDecimalSeparator = (rawValue, currentLocation) => {
  return currentLocation?.locale !== "en-US"
    ? rawValue?.toString().replace(".", ",")
    : rawValue;
};
